import React, { useEffect, useState } from 'react';
import PageGridItem from '../../Shared/Page/PageGridItem';
import DetailTextbox from './DetailTextbox';
import Tooltip from '../../../components/popups/Tooltip';
import FlexWrapper from '../../FlexWrapper';
import Checkbox from '../../../components/Checkbox';
import { pinsByNote, placeLabel } from '../../../Utils/footnotePins';


const DisclosureList = ({ data, onPin, onUnpinPlace, pinSelected, selectedNotes, pinnedElements, locatedFootnote, setLocatedFootnote, activeNotes, newSelectedNotes, setNewSelectedNotes }) => {
	const [notesList, setNotesList] = useState([])
	const [noActiveNotes, setNoActiveNotes] = useState(false)
	const [noInactive, setNoInactive] = useState(false)

	useEffect(() => {
		// one entry per note: a note pinned to several places lists them (code-plan/qfu/plan-footnoteReuse.md)
		const newList = [...pinsByNote(pinnedElements), ...data.filter(n => !selectedNotes.includes(n.id))]
		const foundActive = newList.find(n => (n.id && n.selected) || n.offerNoteId)
		const foundInactive = newList.find(n => n.id && !n.selected)
		setNotesList(newList)
		setNoActiveNotes(foundActive ? false : true)
		setNoInactive(foundInactive ? false : true)
	}, [data, pinnedElements])

	// A place is named from the page (placeLabel), which may still be loading when the list first draws: draw it
	// again shortly, a few times, while a place's element is not on the page yet. Only a note in several places
	// shows its places' names.
	const [, setLabelPass] = useState(0)
	useEffect(() => {
		let pass = 0
		let timer
		const recheck = () => {
			const missing = notesList.some(note => note.places?.length > 1 && note.places.some(place => !document.getElementById(place)))
			if (!missing || pass++ >= 5) return
			setLabelPass(n => n + 1)
			timer = setTimeout(recheck, 1000)
		}
		timer = setTimeout(recheck, 500)
		return () => clearTimeout(timer)
	}, [notesList])

	function scrollToSuperscript(id) {
		if (locatedFootnote === id) setLocatedFootnote(null)
		else {
			const element = document.getElementById(id)
			if (!element) return // a place not on the page (a suppressed row, a target the template no longer has)
			setLocatedFootnote(id)
			element.style.outline = 'solid 2px #0E8EFD'

			element.scrollIntoView({ behavior: "smooth", block: "center" });
			setTimeout(() => {
				setLocatedFootnote(null)
			}, 3000);

			setTimeout(() => {
				element.style.outline = 'solid 2px transparent'
			}, 2000);
		}
	}


	if (activeNotes && noActiveNotes) {
		return <FlexWrapper fullWidth justify="center" align="center" gap="m">
			<p style={{ color: "#adb1bd" }}>No active notes to display</p>
		</FlexWrapper>
	}

	if (!activeNotes && noInactive) {
		return <FlexWrapper fullWidth justify="center" align="center" gap="m">
			<p style={{ color: "#adb1bd" }}>No inactive notes to display</p>
		</FlexWrapper>
	}

	return notesList?.map(note => {
		const { disclosure, noteText, id, offerNoteId, selected, superscript, footnoteId, places } = note
		const noteId = id || offerNoteId
		const text = disclosure || noteText
		const pinned = offerNoteId ? true : false

		if (activeNotes) {
			if ((selected && id) || superscript) return (
				<PageGridItem container direction='row' gap='s' style={{ paddingBottom: '1rem' }}>
					<FlexWrapper direction="column" gap="xs" style={{ height: "100%", padding: "0.5rem 0rem" }}>
						<Tooltip placement="left" label={pinSelected === noteId ? "Cancel" : pinned ? "Unpin" : "Pin"}>
							<span onClick={() => onPin(noteId, pinned && pinSelected !== noteId)} style={{ fontSize: 16 }} className={`material-symbols-outlined disclosure-pin-icon ${pinned ? 'pinned-note' : pinSelected === noteId ? "actively-pinning" : ""}`} >keep</span>
						</Tooltip>
						{footnoteId &&
							<Tooltip placement="left" label='Locate note'>
								<div className='foootnote-locator' style={{ fontSize: 16 }} onClick={() => scrollToSuperscript(footnoteId)}>{superscript}</div>
							</Tooltip>
						}
						{pinned &&
							<Tooltip placement="left" label={pinSelected === noteId ? "Cancel" : "Pin to another place"}>
								<span onClick={() => onPin(noteId, false)} style={{ fontSize: 16 }} className={`material-symbols-outlined disclosure-pin-icon ${pinSelected === noteId ? "actively-pinning" : ""}`} >add</span>
							</Tooltip>
						}
					</FlexWrapper>
					{/* the note's shaded box fills the card's height, beside the pin, number and + icons */}
					<FlexWrapper direction="column" gap="xs" fullWidth align="stretch">
						<div style={{ flex: 1, display: 'flex' }}>
							<DetailTextbox
								value={text}
								update={() => null}
								editMode={false}
								placeholder={'Add notes'}
								minHeight={50}
								resize='vertical'
								className='box-style'
								boxStyle
								fullWidth
								divClassName={(!selected && id) ? 'no-selected-box' : null}
							/>
						</div>
						{places && places.length > 1 &&
							<FlexWrapper direction="column" gap="xs" fullWidth align="flex-start" style={{ fontSize: 12, color: '#6b7280' }}>
								<p>Pinned in {places.length} places</p>
								{places.map((place, index) => (
									<FlexWrapper key={place} direction="row" gap="xs" align="center">
										<Tooltip placement="left" label='Locate'>
											<span className='cursor-pointer' onClick={() => scrollToSuperscript(place)}>{placeLabel(document.getElementById(place), index)}</span>
										</Tooltip>
										{onUnpinPlace &&
											<Tooltip placement="left" label='Unpin this place'>
												<span onClick={() => onUnpinPlace(noteId, place)} style={{ fontSize: 14 }} className='material-symbols-outlined cursor-pointer'>close</span>
											</Tooltip>
										}
									</FlexWrapper>
								))}
							</FlexWrapper>
						}
					</FlexWrapper>
				</PageGridItem>
			)
		} else {
			if (!selected && id) return (
				<PageGridItem container direction='row' gap='s' style={{ paddingBottom: '1rem' }}>
					<FlexWrapper direction="column" gap="xs" style={{ height: "100%", padding: "0.5rem 0rem" }}>
						<Checkbox checked={selected || (newSelectedNotes.length > 0 && newSelectedNotes.includes(id))}
							onChange={() => setNewSelectedNotes(newSelectedNotes.length > 0 ? newSelectedNotes.includes(id) ? newSelectedNotes.filter(n => n !== id) : [...newSelectedNotes, id] : [id])}
							className="delete-icon no-margin"
						/>
					</FlexWrapper>
					<DetailTextbox
						value={text}
						update={() => null}
						editMode={false}
						placeholder={'Add notes'}
						minHeight={50}
						resize='vertical'
						className='box-style'
						boxStyle
						fullWidth
						divClassName={(!selected && id) ? 'no-selected-box' : null}
					/>
				</PageGridItem>
			)
		}
	})
}

export default DisclosureList;